import { useTranslation } from 'react-i18next';

// Components
import { ColumnManagement, ColumnManagementSection, ColumnManagementSectionList } from '../ColumnManagement/ColumnManagement';
import {
  CustomVueSectionTitle,
  CustomVueSectionContent,
} from '../../atoms/CustomVueSection/CustomVueSection';
import SearchField from '../../molecules/SearchField/SearchField';
import ListItem from '../../molecules/ListItem/ListItem';
import ColumnManagementDndList from './ColumnManagementDndList';
import IconButton from '../../atoms/IconButton/IconButton';
import ColumnsVisibility from '../../molecules/ColumnsVisibility/ColumnsVisibility';
import GroupList from '../../molecules/GroupList/GroupList';
import Button from '../../atoms/Button/Button';
import CustomVueListGroup, { CustomVueListGroupList } from '../CustomVueListGroup/CustomVueListGroup';

// Domain
import { areColumnFiltersSameNonEmptyGroupId } from '../../../domain/tableColumnManagement/tableColumnManagementUseCase';

// Styles
import styles from './FilterColumnManagement.module.css';

/**
 * Filter Column Management component
 * @param {object} params
 * @param {import('../../../domain/tableColumnManagement/useTableColumnManagement').useTableColumnManagementReturn} params.columnManagement
 * @param {boolean} params.isMutateTableFiltersOptimisticPending
 * @param {() => void} params.handleColumnsManagementMutation
 * @returns {React.ReactNode}
 */
function FilterColumnManagement({
  columnManagement,
  isMutateTableFiltersOptimisticPending,
  handleColumnsManagementMutation,
}) {
  const { t } = useTranslation();

  const {
    columnFiltersLocal,
    tableColumnsHiddenGroups,
    handleSetColumnPreference,
    handleSetColumnsOrder,
    handleSetColumnPreferenceGroup,
    handleCancelColumnsManagement,
    searchHiddenGroups,
    setSearchHiddenGroups,
  } = columnManagement;

  // Si columnFiltersLocal est vide, afficher le message dans le popover
  if (columnFiltersLocal.length === 0) {
    return (
      <CustomVueListGroup>
        <CustomVueListGroupList>
          <div className={styles['empty-message']}>
            {t('table.filters.noAdministrableColumns')}
          </div>
        </CustomVueListGroupList>
      </CustomVueListGroup>
    );
  }

  return (
    <ColumnManagement
      leftChildren={(
        <ColumnManagementSection>
          <CustomVueSectionTitle>
            {t('table.filters.columnsPin')}
          </CustomVueSectionTitle>
          <ColumnManagementSectionList>
            <ColumnManagementDndList
              columnFiltersLocal={columnFiltersLocal}
              onSetColumnsOrder={handleSetColumnsOrder}
              onSetColumnPreference={handleSetColumnPreference}
              onSetColumnPreferenceGroup={handleSetColumnPreferenceGroup}
            />
          </ColumnManagementSectionList>
        </ColumnManagementSection>
      )}
      rightChildren={(
        <ColumnManagementSection>
          <CustomVueSectionTitle>
            {t('table.filters.columnsVisibility')}
          </CustomVueSectionTitle>
          <CustomVueSectionContent>
            <SearchField
              id="table-columns-visibility-search"
              placeholder={t('table.filters.columnsVisibilitySearch')}
              onFocus={() => {}}
              disabled={false}
              value={searchHiddenGroups}
              onChange={(e) => setSearchHiddenGroups(e.target.value)}
            />
          </CustomVueSectionContent>
          <ColumnManagementSectionList>
            <ColumnsVisibility
              options={tableColumnsHiddenGroups}
              messageNoOptions={t('table.filters.noColumns')}
            >
              {(label, options) => {
                const showGroupOnlyEye = areColumnFiltersSameNonEmptyGroupId(options);
                return (
                  <GroupList
                    key={label}
                    label={label}
                    items={options}
                    action={(
                      <IconButton
                        icon="eye"
                        color="transparent"
                        size="medium"
                        onClick={() => handleSetColumnPreferenceGroup(options, {
                          visible: true,
                        })}
                      />
                    )}
                  >
                    {(option) => {
                      const showColumn = () => handleSetColumnPreference(option.id, {
                        visible: true,
                      });
                      return (
                        <ListItem
                          key={option.id}
                          id={option.id}
                          isDisabled
                          label={option.lib}
                          color={option.color}
                          onClick={showColumn}
                        >
                          {!showGroupOnlyEye ? (
                            <IconButton
                              icon="eye"
                              color="transparent"
                              size="medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                showColumn();
                              }}
                            />
                          ) : null}
                        </ListItem>
                      );
                    }}
                  </GroupList>
                );
              }}
            </ColumnsVisibility>
          </ColumnManagementSectionList>
        </ColumnManagementSection>
      )}
    >
      <>
        <Button
          color="primary100"
          size="small"
          onClick={handleCancelColumnsManagement}
        >
          {t('table.filters.cancel')}
        </Button>

        <Button
          size="small"
          isSubmit
          isLoading={isMutateTableFiltersOptimisticPending}
          onClick={handleColumnsManagementMutation}
        >
          {t('table.filters.apply')}
        </Button>
      </>
    </ColumnManagement>
  );
}

export default FilterColumnManagement;
